import { Badge } from "@musa.tech/design-system";
import type { ReleaseStatus } from "@/integrations/faturamento/client";
import { AWAITING_PO_CONFIG, MEASUREMENT_STATUS_CONFIG } from "./measurementStatus";

interface MeasurementStatusBadgeProps {
  releaseStatus: ReleaseStatus;
  /**
   * Quebra "Aguardando aprovação"/"Aguardando faturamento" em duas linhas menores — pensado para
   * a coluna estreita do extrato. `false` no detalhamento (Fatura/Medição), onde há espaço de
   * sobra e a pílula de uma linha só combina com a do `InvoiceStatusBadge` ao lado do título.
   */
  wrap?: boolean;
  /**
   * Janela vencida sem PO (EDD-582). O estado do ciclo segue "aguardando", mas o que falta é a PO —
   * o selo troca o rótulo para o gerador saber o que fazer.
   */
  awaitingPo?: boolean;
}

export function MeasurementStatusBadge({
  releaseStatus,
  wrap = true,
  awaitingPo = false,
}: MeasurementStatusBadgeProps) {
  const config = awaitingPo ? AWAITING_PO_CONFIG : MEASUREMENT_STATUS_CONFIG[releaseStatus];
  if (!config) return null;

  if (!wrap) {
    return (
      <Badge variant={config.variant} className="whitespace-nowrap">
        {config.label}
      </Badge>
    );
  }

  // "Aguardando aprovação"/"Aguardando faturamento" quebram em duas linhas por pedido de
  // produto — as demais (uma palavra só) não têm onde quebrar, então o split não muda nada.
  // `\n` + `whitespace-pre-line` (em vez de `<br/>`) mantém o rótulo num único nó de texto, que
  // é o que `getByText` normaliza de volta para "Aguardando faturamento" nos testes.
  const [firstWord, ...rest] = config.label.split(' ');
  const secondLine = rest.join(' ');
  const label = secondLine ? `${firstWord}\n${secondLine}` : config.label;

  // O texto do Badge é renderizado por um `Text` interno fixo em `text-sm` — `text-xs` no
  // Badge não alcança esse filho por herança (ele já tem o próprio tamanho explícito), daí o
  // seletor `[&>p]` para sobrescrever o `<p>` do `Text` diretamente.
  return (
    <Badge
      variant={config.variant}
      className="h-auto whitespace-pre-line py-1 text-center [&>p]:text-xs [&>p]:leading-tight"
    >
      {label}
    </Badge>
  );
}
